import React from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import { useAuth } from '../../contexts/AuthContext';
import { useMeDashboard } from '../../hooks/useDashboard';
import { IconLoader2 } from '@tabler/icons-react';

const COLORS = [
    'var(--chart-color1)',
    'var(--chart-color2)',
    'var(--chart-color3)',
    'var(--chart-color4)',
    'var(--chart-color5)',
];

export default function MyLeaveBalanceHR() {
    const { user } = useAuth();
    const orgId = user?.organization?.id;

    const { data: dashboardData, isLoading } = useMeDashboard(orgId);
    const myLeaveBalances = dashboardData?.leave_balances ?? [];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center gap-10">
                    <IconLoader2 size={32} className="animate-spin text-primary" />
                    <p className="text-sm text-text-secondary">Loading your leave balances...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-20">
            <Breadcrumb title="My Leave Balance" path="Leave / My Balance" />

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-20'>
                {myLeaveBalances.length === 0 ? (
                    <div className="col-span-2 text-center py-40 text-text-secondary text-sm bg-bg-dark rounded-xl border border-border">
                        No leave balances found for your account.
                    </div>
                ) : myLeaveBalances.map((lb, i) => {
                    const total = parseFloat(lb.allocated ?? 1);
                    const used = parseFloat(lb.used ?? 0);
                    const remaining = parseFloat(lb.available ?? (total - used));
                    const pct = Math.min(100, Math.round((used / total) * 100));
                    const color = COLORS[i % COLORS.length];
                    return (
                        <div key={i} className='bg-bg-dark rounded-xl border border-border p-20 shadow-light'>
                            <div className='flex items-start justify-between mb-15'>
                                <div>
                                    <p className='text-[13px] font-semibold mb-3'>{lb.leave_type_name ?? lb.leave_type}</p>
                                    <p className='text-xs text-text-secondary'>{used} day{used !== 1 ? 's' : ''} used</p>
                                </div>
                                <div className='text-right'>
                                    <p className='text-[28px] font-bold leading-none' style={{ color }}>{remaining}</p>
                                    <p className='text-xs text-text-secondary mt-3'>of {total} days left</p>
                                </div>
                            </div>
                            <div className='w-full bg-bg-light rounded-full h-6'>
                                <div
                                    className='h-6 rounded-full transition-all duration-500'
                                    style={{ width: `${pct}%`, backgroundColor: color }}
                                />
                            </div>
                            <div className='flex justify-between mt-8'>
                                <span className='text-[10px] text-text-secondary'>0</span>
                                <span className='text-[10px] text-text-secondary font-medium'>{pct}% used</span>
                                <span className='text-[10px] text-text-secondary'>{total}</span>
                            </div>
                            <div className='flex items-center justify-between mt-10 pt-10 border-t border-border text-xs'>
                                <span className='text-text-secondary'>Total Allocated</span>
                                <span className='font-semibold' style={{ color }}>{total} days</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
